import { useState, useEffect, useMemo, useRef } from "react";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, X, Search, Filter, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { defaultCategories } from "@/components/timer/CategorySelect";

interface Project {
  id: string;
  name: string;
}

export interface TimerFilters {
  startDate: Date | null;
  endDate: Date | null;
  projectId: string;
  category: string;
  searchText: string;
  billableOnly: boolean | null;
}

interface TimerFiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TimerFilters;
  onFiltersChange: (filters: TimerFilters) => void;
  projects: Project[];
}

const presetRanges = [
  { label: "Hoje", getValue: () => ({ start: new Date(), end: new Date() }) },
  { label: "Ontem", getValue: () => ({ start: subDays(new Date(), 1), end: subDays(new Date(), 1) }) },
  { label: "Últimos 7 dias", getValue: () => ({ start: subDays(new Date(), 6), end: new Date() }) },
  { label: "Esta semana", getValue: () => ({ start: startOfWeek(new Date(), { locale: ptBR }), end: endOfWeek(new Date(), { locale: ptBR }) }) },
  { label: "Este mês", getValue: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
  { label: "Mês passado", getValue: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: "Últimos 30 dias", getValue: () => ({ start: subDays(new Date(), 29), end: new Date() }) },
];

/** Count how many non-date filters are active */
export function getActiveFiltersCount(filters: TimerFilters): number {
  return [
    filters.projectId && filters.projectId !== "__all__",
    filters.category && filters.category !== "__all__",
    filters.searchText?.trim(),
    filters.billableOnly !== null && filters.billableOnly !== undefined,
  ].filter(Boolean).length;
}

/** Check if filters have a non-default date range (not just "today") */
export function hasCustomDateRange(filters: TimerFilters): boolean {
  if (!filters.startDate || !filters.endDate) return false;
  const today = new Date();
  return !(isSameDay(filters.startDate, today) && isSameDay(filters.endDate, today));
}

export function TimerFiltersDialog({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  projects,
}: TimerFiltersDialogProps) {
  const [localFilters, setLocalFilters] = useState<TimerFilters>(filters);
  const prevOpenRef = useRef(false);

  // Sync local state ONLY when dialog transitions from closed → open
  // Do NOT depend on `filters` reference - it changes every render (timer re-renders every 1s)
  // which would reset any local changes the user made in the dialog
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setLocalFilters({
        ...filters,
        startDate: filters.startDate ? new Date(filters.startDate) : null,
        endDate: filters.endDate ? new Date(filters.endDate) : null,
      });
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleApply = () => {
    onFiltersChange(localFilters);
    onOpenChange(false);
  };

  const handleClear = () => {
    const cleared: TimerFilters = {
      startDate: new Date(),
      endDate: new Date(),
      projectId: "",
      category: "",
      searchText: "",
      billableOnly: null,
    };
    setLocalFilters(cleared);
    onFiltersChange(cleared);
    onOpenChange(false);
  };

  const handlePresetClick = (preset: typeof presetRanges[0]) => {
    const range = preset.getValue();
    setLocalFilters(prev => ({
      ...prev,
      startDate: range.start,
      endDate: range.end,
    }));
  };

  // Detect which preset matches the current date range
  const activePresetLabel = useMemo(() => {
    if (!localFilters.startDate || !localFilters.endDate) return null;
    for (const preset of presetRanges) {
      const range = preset.getValue();
      if (
        isSameDay(localFilters.startDate, range.start) &&
        isSameDay(localFilters.endDate, range.end)
      ) {
        return preset.label;
      }
    }
    return null;
  }, [localFilters.startDate, localFilters.endDate]);

  const activeFiltersCount = getActiveFiltersCount(localFilters);
  const hasDateRange = hasCustomDateRange(localFilters);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            Filtrar Entradas
            {(activeFiltersCount > 0 || hasDateRange) && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {activeFiltersCount + (hasDateRange ? 1 : 0)} {activeFiltersCount + (hasDateRange ? 1 : 0) === 1 ? "filtro" : "filtros"}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Date Range Presets */}
          <div className="space-y-2.5">
            <Label className="text-sm font-medium">Período Rápido</Label>
            <div className="flex flex-wrap gap-2">
              {presetRanges.map((preset) => {
                const isActive = activePresetLabel === preset.label;
                return (
                  <Button
                    key={preset.label}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "rounded-full text-xs h-7 px-3 transition-all",
                      isActive && "shadow-sm"
                    )}
                    onClick={() => handlePresetClick(preset)}
                  >
                    {preset.label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Custom Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Data Inicial</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal rounded-xl h-10",
                      !localFilters.startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {localFilters.startDate
                      ? format(localFilters.startDate, "dd/MM/yyyy")
                      : "Selecione"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={localFilters.startDate || undefined}
                    onSelect={(date) =>
                      setLocalFilters((prev) => ({ ...prev, startDate: date || null }))
                    }
                    locale={ptBR}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Data Final</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal rounded-xl h-10",
                      !localFilters.endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {localFilters.endDate
                      ? format(localFilters.endDate, "dd/MM/yyyy")
                      : "Selecione"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={localFilters.endDate || undefined}
                    onSelect={(date) =>
                      setLocalFilters((prev) => ({ ...prev, endDate: date || null }))
                    }
                    locale={ptBR}
                    disabled={(date) =>
                      localFilters.startDate ? date < localFilters.startDate : false
                    }
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Project Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Projeto</Label>
            <Select
              value={localFilters.projectId || "__all__"}
              onValueChange={(v) =>
                setLocalFilters((prev) => ({ ...prev, projectId: v === "__all__" ? "" : v }))
              }
            >
              <SelectTrigger className="rounded-xl h-10">
                <SelectValue placeholder="Todos os projetos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os projetos</SelectItem>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Categoria</Label>
            <Select
              value={localFilters.category || "__all__"}
              onValueChange={(v) =>
                setLocalFilters((prev) => ({ ...prev, category: v === "__all__" ? "" : v }))
              }
            >
              <SelectTrigger className="rounded-xl h-10">
                <SelectValue placeholder="Todas as categorias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas as categorias</SelectItem>
                {defaultCategories
                  .filter((c) => c.id !== "custom")
                  .map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Text Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Buscar na descrição</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={localFilters.searchText}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, searchText: e.target.value }))
                }
                placeholder="Digite para buscar..."
                className="rounded-xl h-10 pl-9"
              />
            </div>
          </div>

          {/* Billable Filter */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Faturável</Label>
            <Select
              value={localFilters.billableOnly === null ? "__all__" : localFilters.billableOnly ? "yes" : "no"}
              onValueChange={(v) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  billableOnly: v === "__all__" ? null : v === "yes",
                }))
              }
            >
              <SelectTrigger className="rounded-xl h-10">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="yes">Apenas faturáveis</SelectItem>
                <SelectItem value="no">Apenas não faturáveis</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex-1 rounded-xl gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Limpar
          </Button>
          <Button onClick={handleApply} className="flex-1 rounded-xl gap-2">
            <Search className="h-4 w-4" />
            Aplicar Filtros
            {activeFiltersCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center rounded-full text-xs bg-primary-foreground/20">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
